import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  Platform,
  Image,
  Modal,
} from 'react-native';
import { appointmentManager, Appointment } from '../utils/appointmentManager';
import { AuthManager, User } from '../utils/authManager';
import UserBottomMenu from '../components/UserBottomMenu';
import EditAppointmentModal from '../components/EditAppointmentModal';
import LiveQueueCard from '../components/LiveQueueCard';
import CustomerQueueStatusCard from '../components/CustomerQueueStatusCard';
import { fetchCustomerAppointmentBusinessPrefs } from '../services/customerQueueVisibilityService';
import {
  DEFAULT_CUSTOMER_QUEUE_VISIBILITY,
  type CustomerQueueVisibility,
} from '../types/customerQueueVisibility';
import type { CustomerAppointmentBusinessPrefs } from '../types/customerPickupSettings';
import {
  getCustomerAppointmentBadge,
  getCustomerPickupAwareStatusLabel,
  getCustomerQueueStatusLine,
} from '../utils/customerVisitDisplay';
import { formatVehicleHandoffLabel } from '../types/automotiveIntake';
import { showCenteredAlert } from '../utils/centeredAlert';
import SimpleStorage from '../utils/simpleStorage';
import { appointmentRequestService } from '../services/appointmentRequestService';
import {
  getFilteredCustomerAppointments,
  appointmentsToCSV,
  downloadCSV,
  groupAppointmentsIntoDateSections,
  type CustomerDateFilter,
} from '../utils/appointmentFilterUtils';
import { AppointmentPrepaidBadge } from '../components/AppointmentPrepaidBadge';

// Web-only globals (safe to declare; only used behind Platform checks)
declare const window: any;

interface UserAppointmentsScreenProps {
  onBack: () => void;
  username?: string;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'favorites' | 'profile') => void;
  onLogout?: () => void;
  onNavigateToChat?: () => void;
  onExploreBusinesses?: () => void;
  /** When provided (e.g. from UserDashboardScreen), use this instead of loading - keeps Bookings tab in sync with home page upcoming card */
  userFromParent?: User | null;
  appointmentsFromParent?: Appointment[];
  /** Optional initial filter (e.g. dashboard deep-link). */
  initialDateFilter?: CustomerDateFilter;
}

const CUSTOMER_FILTER_LABELS: Record<CustomerDateFilter, string> = {
  upcoming: 'Upcoming',
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  past: 'Past',
};

const toLocalDateKey = (dateInput: string): string => {
  if (!dateInput) return '';
  const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return dateInput;
  const dt = new Date(dateInput);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DELETE_CONFIRM_MESSAGE =
  'This appointment will be permanently deleted and cannot be retrieved. Are you sure you want to delete?';

const UserAppointmentsScreen: React.FC<UserAppointmentsScreenProps> = ({
  onBack,
  username: _username,
  onTabPress,
  onLogout,
  onNavigateToChat,
  onExploreBusinesses,
  userFromParent,
  appointmentsFromParent,
  initialDateFilter,
}) => {
  const [appointments, setAppointments] = useState<Appointment[]>(() => appointmentsFromParent ?? []);
  const [allAppointmentsCache, setAllAppointmentsCache] = useState<Appointment[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<any>(null);
  const [dateFilter, setDateFilter] = useState<CustomerDateFilter>(initialDateFilter ?? 'upcoming');
  const [queueRefreshing, setQueueRefreshing] = useState(false);
  const [lastQueueRefreshAt, setLastQueueRefreshAt] = useState<Date | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [expandedAppointmentId, setExpandedAppointmentId] = useState<string | null>(null);
  const [businessAppointmentPrefs, setBusinessAppointmentPrefs] = useState<
    Record<string, CustomerAppointmentBusinessPrefs>
  >({});
  const [liveQueueAppointmentsByBusiness, setLiveQueueAppointmentsByBusiness] = useState<Record<string, Appointment[]>>({});

  // Always render from local `appointments` state. Parent props are copied into it via useEffect
  // so pull-to-refresh and merged Firestore/local loads actually update the list (previously the UI
  // kept showing stale `appointmentsFromParent` forever).
  const effectiveAppointments = appointments;

  const queueVisibilityByBusiness = useMemo(() => {
    const o: Record<string, CustomerQueueVisibility> = {};
    Object.entries(businessAppointmentPrefs).forEach(([id, p]) => {
      o[id] = p.customerQueueVisibility;
    });
    return o;
  }, [businessAppointmentPrefs]);

  const filteredAppointments = useMemo(
    () => getFilteredCustomerAppointments(effectiveAppointments, dateFilter),
    [effectiveAppointments, dateFilter]
  );

  const appointmentDateSections = useMemo(
    () =>
      groupAppointmentsIntoDateSections(filteredAppointments, {
        descending: dateFilter === 'past',
      }),
    [filteredAppointments, dateFilter]
  );

  useEffect(() => {
    setExpandedAppointmentId(null);
  }, [dateFilter]);

  useEffect(() => {
    if (userFromParent === undefined) loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When parent passes user/appointments, use them immediately (no loading)
  useEffect(() => {
    if (userFromParent !== undefined && appointmentsFromParent !== undefined) {
      setAppointments(appointmentsFromParent);
      setUser(userFromParent ?? null);
      setLoading(false);
    }
  }, [userFromParent, appointmentsFromParent]);

  // Allow parent to deep-link to a specific filter (e.g. "today")
  useEffect(() => {
    if (initialDateFilter) setDateFilter(initialDateFilter);
  }, [initialDateFilter]);

  // Load appointments after user data is loaded when not using parent data
  useEffect(() => {
    if (userFromParent === undefined && user) {
      loadAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userFromParent]);

  // Listen for real-time status updates
  // Use capability detection only: never touch window.addEventListener unless it exists.
  // Platform.OS can be wrong in some builds; RN has window (as global) but addEventListener is undefined.
  useEffect(() => {
    const canUseWindowEvents = ((): boolean => {
      try {
        const g = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
        const w = g?.window;
        return !!(w && typeof w.addEventListener === 'function');
      } catch {
        return false;
      }
    })();

    if (!canUseWindowEvents) {
      const pollInterval = setInterval(() => {
        if (user) loadAppointments({ showLoading: false });
      }, 60000);
      return () => clearInterval(pollInterval);
    }

    try {
      const g = globalThis as any;
      const w = g?.window;
      if (!w || typeof w.addEventListener !== 'function') {
        const pollInterval = setInterval(() => {
          if (user) loadAppointments({ showLoading: false });
        }, 60000);
        return () => clearInterval(pollInterval);
      }

      const handleStatusUpdate = (event: any) => {
        if (event?.detail) console.log('📬 Appointment status update received:', event.detail);
        loadAppointments({ showLoading: false });
      };
      w.addEventListener('appointmentStatusUpdate', handleStatusUpdate);

      const checkForUpdates = () => {
        try {
          if (typeof w.localStorage === 'undefined' || w.localStorage == null) return;
          const keys = Object.keys(w.localStorage);
          const updateKeys = keys.filter((key: string) => key.startsWith('appointment_update_'));
          if (updateKeys.length > 0) {
            loadAppointments({ showLoading: false });
            updateKeys.forEach((key: string) => w.localStorage.removeItem(key));
          }
        } catch (_) {
          // localStorage not available (e.g. RN)
        }
      };
      const updateInterval = setInterval(checkForUpdates, 60000);

      return () => {
        try {
          if (typeof w.removeEventListener === 'function') {
            w.removeEventListener('appointmentStatusUpdate', handleStatusUpdate);
          }
        } catch (_) {}
        clearInterval(updateInterval);
      };
    } catch (_) {
      const pollInterval = setInterval(() => {
        if (user) loadAppointments({ showLoading: false });
      }, 60000);
      return () => clearInterval(pollInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const businessIdsKey = useMemo(() => {
    const ids = effectiveAppointments
      .map((a) => a.businessOwnerId)
      .filter((id): id is string => !!id && typeof id === 'string');
    return [...new Set(ids)].sort().join(',');
  }, [effectiveAppointments]);

  useEffect(() => {
    let cancelled = false;
    const ids = businessIdsKey
      ? (businessIdsKey.split(',').filter(Boolean) as string[])
      : [];
    if (ids.length === 0) {
      setBusinessAppointmentPrefs({});
      return;
    }
    (async () => {
      try {
        const map = await fetchCustomerAppointmentBusinessPrefs(ids);
        if (!cancelled) setBusinessAppointmentPrefs(map);
      } catch {
        if (!cancelled) setBusinessAppointmentPrefs({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessIdsKey]);

  useEffect(() => {
    let cancelled = false;
    const todayKey = toLocalDateKey(new Date().toISOString());
    const businessIdsForLiveQueue = [...new Set(
      effectiveAppointments
        .filter((apt) => !apt.id?.startsWith('req_'))
        .filter((apt) => toLocalDateKey(apt.appointmentDate) === todayKey)
        .map((apt) => apt.businessOwnerId)
        .filter((id): id is string => !!id && typeof id === 'string')
    )];

    if (businessIdsForLiveQueue.length === 0) {
      setLiveQueueAppointmentsByBusiness({});
      return;
    }

    (async () => {
      try {
        const entries = await Promise.all(
          businessIdsForLiveQueue.map(async (businessId) => {
            const list = await appointmentManager.getAppointmentsByDateRange(
              businessId,
              todayKey,
              todayKey,
              { limit: 500 }
            );
            return [businessId, list] as const;
          })
        );
        if (!cancelled) {
          const next: Record<string, Appointment[]> = {};
          entries.forEach(([businessId, list]) => {
            next[businessId] = Array.isArray(list) ? list : [];
          });
          setLiveQueueAppointmentsByBusiness(next);
        }
      } catch {
        if (!cancelled) {
          setLiveQueueAppointmentsByBusiness({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveAppointments, lastQueueRefreshAt]);

  const loadUserData = async () => {
    try {
      // Add timeout for user data loading
      const userPromise = AuthManager.getSavedUser();
      const timeoutPromise = new Promise<User | null>((resolve) => 
        setTimeout(() => {
          console.warn('⚠️ User data loading timeout');
          resolve(null);
        }, 3000) // 3 second timeout for user data
      );
      
      const userData = await Promise.race([userPromise, timeoutPromise]);
      setUser(userData);
      
      // If no user data after timeout, still try to load appointments (will show empty)
      if (!userData) {
        console.warn('⚠️ No user data found, loading appointments with empty user');
        setTimeout(() => {
          setLoading(false); // Ensure loading is cleared even without user
        }, 100);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Clear loading on error
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  const loadAppointments = async (options?: { showLoading?: boolean }) => {
    const shouldShowLoading = options?.showLoading ?? true;
    try {
      if (shouldShowLoading) {
        setLoading(true);
      }
      
      // Ensure user is loaded before loading appointments
      if (!user) {
        console.log('⚠️ UserAppointmentsScreen: User not loaded yet, skipping appointment load');
        setAppointments([]);
        setLoading(false); // Ensure loading is cleared
        return;
      }
      
      // Load regular appointments with timeout protection
      let allAppointments: Appointment[] = [];
      try {
        // Use Promise.race with timeout
        const appointmentsPromise = appointmentManager.getAppointments();
        const timeoutPromise = new Promise<Appointment[]>((resolve) => 
          setTimeout(() => {
            console.warn('⚠️ Appointments loading timeout, using empty array');
            resolve([]);
          }, 8000) // Increased to 8 seconds to reduce timeout issues
        );
        
        allAppointments = await Promise.race([appointmentsPromise, timeoutPromise]);
      } catch (error) {
        console.warn('⚠️ Error loading appointments:', error);
        allAppointments = []; // Default to empty array
      }
      
      // Ensure allAppointments is an array
      if (!Array.isArray(allAppointments)) {
        allAppointments = [];
      }
      setAllAppointmentsCache(allAppointments);
      
      // Filter appointments for this user by email or name
      let filteredAppointments: Appointment[] = allAppointments.filter(apt => {
        if (!apt) return false; // Skip null/undefined appointments
        const userId = (user as any).id || (user as any).uid || '';
        const userEmail = (user.email || '').toLowerCase().trim();
        const userUsername = (user.username || '').toLowerCase().trim();
        const userPhone = (user.phone || '').trim();
        const userName = (user.name || '').trim();
        const aptCustomerId = ((apt as any).customerId || '').trim();
        const aptEmail = (apt.email || '').toLowerCase().trim();
        const aptPhone = (apt.phone || '').trim();
        const aptName = (apt.customerName || '').trim();

        const idMatch = !!userId && !!aptCustomerId && aptCustomerId === userId;
        const emailMatch = !!aptEmail && (aptEmail === userEmail || aptEmail === userUsername);
        const phoneMatch = !!aptPhone && !!userPhone && aptPhone === userPhone;
        const nameMatch = !!aptName && !!userName && aptName === userName;

        return idMatch || emailMatch || phoneMatch || nameMatch;
      });
      
      // Load pending appointment requests and convert them to appointments
      try {
        const userId = user.id || (user as any).uid || '';
        const userEmail = user.email || '';
        const userPhone = user.phone || '';
        
        // Load appointment requests with timeout protection
        let allRequests: any[] = [];
        try {
          const requestsPromise = appointmentRequestService.getAllRequests();
          const timeoutPromise = new Promise<any[]>((resolve) => 
            setTimeout(() => {
              console.warn('⚠️ Appointment requests loading timeout, using empty array');
              resolve([]);
            }, 8000) // Increased to 8 seconds to reduce timeout issues
          );
          
          allRequests = await Promise.race([requestsPromise, timeoutPromise]);
        } catch (error) {
          console.warn('⚠️ Error loading appointment requests:', error);
          allRequests = []; // Default to empty array
        }
        
        // Ensure allRequests is an array
        if (!Array.isArray(allRequests)) {
          allRequests = [];
        }
        
        const userRequests = allRequests.filter(req => {
          if (!req) return false; // Skip null/undefined requests
          return req.userId === userId || 
                 req.userEmail?.toLowerCase() === userEmail.toLowerCase() ||
                 req.userPhone === userPhone;
        });
        
        // Convert appointment requests to appointment format for display
        const requestAppointments: Appointment[] = userRequests
          .filter(req => 
            req.status === 'pending' || 
            req.status === 'confirmed' || 
            req.status === 'reschedule_requested' ||
            req.status === 'business_will_call' ||
            req.status === 'declined' ||
            req.status === 'expired'
          )
          .map(req => ({
            // Prefix to avoid collisions with locally-stored appointment ids and keep React keys stable
            id: `req_${req.id}`,
            customerName: user?.name || 'You',
            customerId: req.userId,
            businessId: `external_${req.businessPhone}`,
            businessName: req.businessName,
            serviceType: req.service,
            appointmentDate: req.preferredDate,
            appointmentTime: req.preferredTime,
            status: req.status === 'pending' ? 'pending_business_confirmation' : 
                    req.status === 'confirmed' ? 'confirmed' : 
                    req.status === 'reschedule_requested' ? 'pending_reschedule' :
                    req.status === 'business_will_call' ? 'business_will_call' :
                    req.status === 'declined' ? 'declined' :
                    req.status === 'expired' ? 'expired' : 'pending',
            address: req.businessLocation || '',
            phone: req.businessPhone,
            email: req.businessEmail || '',
            createdAt: req.createdAt,
            updatedAt: req.updatedAt,
            // Additional fields for display
            specialInstructions: req.specialRequests || '',
          }));
        
        // Combine regular appointments with request appointments
        filteredAppointments = [...filteredAppointments, ...requestAppointments];
        
        // Sort by date (most recent first)
        filteredAppointments.sort((a, b) => {
          const dateA = new Date(a.appointmentDate).getTime();
          const dateB = new Date(b.appointmentDate).getTime();
          return dateB - dateA;
        });
      } catch (error) {
        console.error('Error processing appointment requests:', error);
        // Continue with regular appointments if request loading fails
      }
      
      console.log('🔍 UserAppointmentsScreen: Loaded appointments:', filteredAppointments.length);
      
      // Always set appointments (even if empty) and clear loading
      setAppointments(filteredAppointments || []);
      setLastQueueRefreshAt(new Date());
      if (shouldShowLoading) {
        setLoading(false); // Clear loading immediately after setting appointments
      }
      
    } catch (error) {
      console.error('❌ Error loading appointments:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      // On error, show empty list and clear loading
      setAppointments([]);
      if (shouldShowLoading) {
        setLoading(false);
      }
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadAppointments({ showLoading: false });
    setRefreshing(false);
  };

  const onQueueRefresh = async () => {
    setQueueRefreshing(true);
    try {
      await loadAppointments({ showLoading: false });
    } finally {
      setQueueRefreshing(false);
    }
  };

  const getQueueInsightForAppointment = (appointment: Appointment) => {
    if (!appointment.businessOwnerId || !appointment.id || appointment.id.startsWith('req_')) return null;
    const source = liveQueueAppointmentsByBusiness[appointment.businessOwnerId] ?? allAppointmentsCache;
    const sameBusiness = source.filter(
      (apt) =>
        apt.businessOwnerId === appointment.businessOwnerId &&
        apt.appointmentDate === appointment.appointmentDate &&
        ((apt.locationId || '') === (appointment.locationId || ''))
    );
    const map = appointmentManager.computeQueueInsights(sameBusiness, {
      businessOwnerId: appointment.businessOwnerId,
      locationId: appointment.locationId,
      workerId: appointment.workerId,
    });
    return map[appointment.id] || null;
  };

  const getLiveQueueState = (appointment: Appointment): 'show' | 'future' | 'hide' => {
    const todayKey = toLocalDateKey(new Date().toISOString());
    const aptDateKey = toLocalDateKey(appointment.appointmentDate);
    if (!aptDateKey || aptDateKey !== todayKey) {
      return aptDateKey > todayKey ? 'future' : 'hide';
    }
    if (appointment.status === 'completed' || appointment.status === 'cancelled' || appointment.status === 'declined') return 'hide';
    if (appointment.queueStage === 'completed' || appointment.queueStage === 'no_show') return 'hide';
    return 'show';
  };

  const getCustomerQueueVisibilityForAppointment = (appointment: Appointment): CustomerQueueVisibility => {
    const id = appointment.businessOwnerId;
    if (!id) return DEFAULT_CUSTOMER_QUEUE_VISIBILITY;
    return queueVisibilityByBusiness[id] ?? DEFAULT_CUSTOMER_QUEUE_VISIBILITY;
  };

  const handleFilterSelect = (filter: CustomerDateFilter) => {
    setDateFilter(filter);
    setShowFilterDropdown(false);
  };

  const handleExportCurrent = () => {
    setShowExportDropdown(false);
    const list = filteredAppointments;
    const csv = appointmentsToCSV(list, false);
    const name = `bookapt-my-appointments-${dateFilter}-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, name);
    if (Platform.OS !== 'web') Alert.alert('Exported', `Saved ${list.length} appointments to ${name}`);
  };

  const handleExportAll = () => {
    setShowExportDropdown(false);
    const csv = appointmentsToCSV(effectiveAppointments.filter(a => a.status !== 'cancelled'), false);
    const name = `bookapt-my-appointments-all-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, name);
    if (Platform.OS !== 'web') Alert.alert('Exported', `Saved ${effectiveAppointments.length} appointments to ${name}`);
  };

  const handleCancelAppointment = async (appointment: Appointment) => {
    const confirmCancel = () => {
      if (Platform.OS === 'web') {
        if (window.confirm(`Are you sure you want to cancel your appointment for ${appointment.serviceType}?`)) {
          performCancel();
        }
      } else {
        Alert.alert(
          'Cancel Appointment',
          `Are you sure you want to cancel your appointment for ${appointment.serviceType}?`,
          [
            { text: 'Keep Appointment', style: 'cancel' },
            {
              text: 'Cancel Appointment',
              style: 'destructive',
              onPress: performCancel,
            },
          ]
        );
      }
    };

    const performCancel = async () => {
      try {
        console.log('🔄 User cancelling appointment:', appointment.id);
        
        // Optimistically update local state first for immediate UI feedback
        setAppointments(prev => 
          prev.map(apt => 
            apt.id === appointment.id 
              ? { ...apt, status: 'cancelled' as const }
              : apt
          )
        );
        
        // Then update in storage/Firestore
        await appointmentManager.updateAppointmentStatus(appointment.id, 'cancelled');
        console.log('✅ Appointment status updated to cancelled');
        
        // Reload appointments to ensure sync with storage/Firestore
        // Use a shorter timeout for reload after cancellation
        try {
          const reloadPromise = loadAppointments();
          const timeoutPromise = new Promise<void>((resolve) => 
            setTimeout(() => {
              console.warn('⚠️ Reload timeout after cancellation, but local state already updated');
              resolve();
            }, 3000) // 3 second timeout for reload
          );
          await Promise.race([reloadPromise, timeoutPromise]);
          console.log('✅ Appointments reloaded');
        } catch (reloadError) {
          console.warn('⚠️ Error reloading appointments after cancellation:', reloadError);
          // Don't fail the cancellation - local state is already updated
        }
        
        if (Platform.OS === 'web') {
          window.alert('Appointment Cancelled\n\nYour appointment has been cancelled successfully.');
        } else {
          Alert.alert('Appointment Cancelled', 'Your appointment has been cancelled successfully.');
        }
      } catch (error) {
        console.error('❌ Error cancelling appointment:', error);
        // Revert optimistic update on error
        setAppointments(prev => 
          prev.map(apt => 
            apt.id === appointment.id 
              ? { ...apt, status: appointment.status }
              : apt
          )
        );
        if (Platform.OS === 'web') {
          window.alert('Error\n\nFailed to cancel appointment. Please try again.');
        } else {
          Alert.alert('Error', 'Failed to cancel appointment. Please try again.');
        }
      }
    };

    confirmCancel();
  };

  const handleDeleteAppointment = async (appointment: Appointment) => {
    if (appointment.id.startsWith('req_')) {
      showCenteredAlert('Cannot Delete', 'This appointment request cannot be deleted here.');
      return;
    }

    const performDelete = async () => {
      try {
        console.log('🗑️ User deleting appointment:', appointment.id);
        setAppointments(prev => prev.filter(apt => apt.id !== appointment.id));
        setExpandedAppointmentId((cur) => (cur === appointment.id ? null : cur));
        await appointmentManager.deleteAppointment(appointment.id);
        try {
          await loadAppointments();
        } catch (reloadError) {
          console.warn('⚠️ Error reloading appointments after deletion:', reloadError);
        }
        showCenteredAlert('Deleted', 'The appointment has been permanently deleted.');
      } catch (error) {
        console.error('❌ Error deleting appointment:', error);
        try {
          await loadAppointments();
        } catch (reloadError) {
          console.warn('⚠️ Error reloading appointments after failed deletion:', reloadError);
        }
        showCenteredAlert('Error', 'Failed to delete appointment. Please try again.');
      }
    };

    showCenteredAlert('Delete Appointment', DELETE_CONFIRM_MESSAGE, [
      { text: 'Keep Appointment', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: performDelete },
    ]);
  };

  const handleRescheduleAppointment = (appointment: Appointment) => {
    // Open modal to reschedule appointment (change date/time)
    console.log('Opening reschedule modal for appointment:', appointment);
    setEditingAppointment(appointment);
    setShowEditModal(true);
  };

  const handleSaveEditedAppointment = async (updatedAppointment: any) => {
    try {
      console.log('Saving edited appointment:', updatedAppointment);
      
      // Update appointments in state
      setAppointments(prev =>
        prev.map(apt => (apt.id === updatedAppointment.id ? updatedAppointment : apt))
      );
      
      // Update in storage
      const allBookingsJson = await SimpleStorage.getItem('all_bookings');
      if (allBookingsJson) {
        const allBookings = JSON.parse(allBookingsJson);
        const updatedBookings = allBookings.map((b: any) =>
          b.id === updatedAppointment.id ? updatedAppointment : b
        );
        await SimpleStorage.setItem('all_bookings', JSON.stringify(updatedBookings));
      }
      
      // Also update user_bookings if it exists
      const userBookingsJson = await SimpleStorage.getItem('user_bookings');
      if (userBookingsJson) {
        const userBookings = JSON.parse(userBookingsJson);
        const updatedUserBookings = userBookings.map((b: any) =>
          b.id === updatedAppointment.id ? updatedAppointment : b
        );
        await SimpleStorage.setItem('user_bookings', JSON.stringify(updatedUserBookings));
      }
      
      // Close modal
      setShowEditModal(false);
      setEditingAppointment(null);
      
      // Show success message
      Alert.alert(
        '✅ Appointment Updated!',
        `Your appointment has been rescheduled to:\n\n${updatedAppointment.appointmentDate}\n🕐 ${updatedAppointment.appointmentTime}\n\nYou'll receive an updated confirmation email.`
      );
    } catch (error) {
      console.error('Error saving edited appointment:', error);
      Alert.alert('Error', 'Failed to save changes. Please try again.');
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#34C759'; // Green
      case 'pending': return '#FF9500'; // Orange
      case 'pending_business_confirmation': return '#FF9500'; // Orange - actively waiting
      case 'pending_reschedule': return '#FF9500'; // Orange
      case 'business_will_call': return '#007AFF'; // Blue
      case 'declined': return '#FF3B30'; // Red
      case 'expired': return '#8E8E93'; // Gray
      case 'completed': return '#34C759'; // Green
      case 'cancelled': return '#FF3B30'; // Red
      case 'paid': return '#6F42C1'; // Purple
      default: return '#8E8E93'; // Gray
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'pending': return 'Pending';
      case 'pending_business_confirmation': return 'Pending Business Confirmation';
      case 'pending_reschedule': return 'Pending Reschedule';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      case 'paid': return 'Paid';
      default: return status;
    }
  };

  const handleTabPress = (tab: any) => {
    // Home button should always go to dashboard, not chat
    if (onTabPress) {
      onTabPress(tab);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse YYYY-MM-DD as local date to avoid timezone issues
    // When you do new Date("2026-01-16"), it's interpreted as UTC midnight
    // which can shift to the previous day in timezones behind UTC
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day); // Create local date
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    // Fallback for other date formats
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatBusinessHours = (hours: any): string => {
    if (!hours) return 'Hours not set';
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayAbbrevs = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const openDays: string[] = [];
    
    daysOfWeek.forEach((day, index) => {
      if (hours[day]?.isOpen && hours[day]?.openTime && hours[day]?.closeTime) {
        openDays.push(`${dayAbbrevs[index]}: ${hours[day].openTime}-${hours[day].closeTime}`);
      }
    });
    
    return openDays.length > 0 ? openDays.join(' | ') : 'Closed';
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>Loading appointments...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>My Appointments</Text>
        <View style={{ width: 60 }} />
      </View>

      {/* Option B: View dropdown + Export */}
      <View style={styles.filterRow}>
        <TouchableOpacity style={styles.filterDropdown} onPress={() => { setShowFilterDropdown(true); setShowExportDropdown(false); }}>
          <Text style={styles.filterDropdownText}>View: {CUSTOMER_FILTER_LABELS[dateFilter]} ▼</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.exportButton} onPress={() => { setShowExportDropdown(true); setShowFilterDropdown(false); }}>
          <Text style={styles.exportButtonText}>Export ▼</Text>
        </TouchableOpacity>
      </View>

      <Modal visible={showFilterDropdown} transparent animationType="fade">
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowFilterDropdown(false)}>
          <View style={styles.filterDropdownMenu}>
            {(['upcoming', 'today', 'yesterday', 'this_week', 'past'] as CustomerDateFilter[]).map((f) => (
              <TouchableOpacity key={f} style={styles.filterDropdownItem} onPress={() => handleFilterSelect(f)}>
                <Text style={[styles.filterDropdownItemText, dateFilter === f && styles.filterDropdownItemTextActive]}>{CUSTOMER_FILTER_LABELS[f]}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal visible={showExportDropdown} transparent animationType="fade">
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowExportDropdown(false)}>
          <View style={styles.exportDropdownMenu}>
            <TouchableOpacity style={styles.exportDropdownItem} onPress={handleExportCurrent}>
              <Text style={styles.exportDropdownItemText}>Export current view (CSV)</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.exportDropdownItem} onPress={handleExportAll}>
              <Text style={styles.exportDropdownItemText}>Export all (CSV)</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>{CUSTOMER_FILTER_LABELS[dateFilter]} ({filteredAppointments.length})</Text>
          {filteredAppointments.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No appointments in this view</Text>
              <Text style={styles.emptyStateSubtext}>Try another filter or pull to refresh</Text>
            </View>
          ) : (
            appointmentDateSections.map((section) => (
              <View key={section.dateKey} style={styles.dateSectionBlock}>
                <Text style={styles.dateSectionHeading}>{section.title}</Text>
                <Text style={styles.dateSectionCount}>
                  {section.items.length} booking{section.items.length === 1 ? '' : 's'}
                </Text>
                {section.items.map((appointment) => {
                  const expanded = expandedAppointmentId === appointment.id;
                  const bid = appointment.businessOwnerId;
                  const pickupPref = bid ? businessAppointmentPrefs[bid] : undefined;
                  return (
                    <View key={appointment.id} style={styles.appointmentCard}>
                      <TouchableOpacity
                        style={styles.compactAppointmentRow}
                        activeOpacity={0.65}
                        onPress={() =>
                          setExpandedAppointmentId((cur) => (cur === appointment.id ? null : appointment.id))
                        }
                      >
                        <View style={styles.compactLeft}>
                          <Text style={styles.compactTime}>{appointment.appointmentTime || '—'}</Text>
                          <Text style={styles.compactService} numberOfLines={2}>
                            {appointment.serviceType}
                          </Text>
                          {!!appointment.businessName && (
                            <Text style={styles.compactBusiness} numberOfLines={1}>
                              {appointment.businessName}
                            </Text>
                          )}
                        </View>
                        <View style={styles.compactRight}>
                          <View style={styles.compactBadgesRow}>
                            {(() => {
                              const badgeOverride = getCustomerAppointmentBadge(appointment, pickupPref);
                              if (badgeOverride) {
                                return (
                                  <View style={[styles.statusBadge, { backgroundColor: badgeOverride.backgroundColor }]}>
                                    <Text style={styles.statusText}>{badgeOverride.text}</Text>
                                  </View>
                                );
                              }
                              return (
                                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(appointment.status) }]}>
                                  <Text style={styles.statusText}>
                                    {getCustomerPickupAwareStatusLabel(
                                      appointment,
                                      pickupPref,
                                      getStatusText(appointment.status)
                                    )}
                                  </Text>
                                </View>
                              );
                            })()}
                            <AppointmentPrepaidBadge appointment={appointment} variant="pill" />
                          </View>
                          <Text style={styles.compactTapHint}>{expanded ? 'Tap to hide' : 'Tap for details'}</Text>
                        </View>
                      </TouchableOpacity>

                      {expanded && (
                        <View style={styles.expandedAppointmentBlock}>
                          <AppointmentPrepaidBadge appointment={appointment} variant="banner" />
                          <View style={styles.appointmentDetails}>
                            <View style={styles.detailRow}>
                              <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                              <Text style={styles.detailText}>{formatDate(appointment.appointmentDate)} at {appointment.appointmentTime}</Text>
                            </View>
                            {appointment.businessName && (
                              <Text style={styles.detailText}>🏢 Business: {appointment.businessName}</Text>
                            )}
                            {appointment.businessAddress && (
                              <Text style={styles.detailText}>📍 {appointment.businessAddress}</Text>
                            )}
                            {appointment.businessPhone && (
                              <Text style={styles.detailText}>📞 {appointment.businessPhone}</Text>
                            )}
                            {appointment.businessHours && (
                              <Text style={styles.detailText}>🕐 Hours: {formatBusinessHours(appointment.businessHours)}</Text>
                            )}
                            {!appointment.businessName && appointment.address && (
                              <Text style={styles.detailText}>📍 {appointment.address}</Text>
                            )}
                            {!appointment.businessPhone && appointment.phone && (
                              <Text style={styles.detailText}>📞 {appointment.phone}</Text>
                            )}
                            {(() => {
                              const queueState = getLiveQueueState(appointment);
                              const vis = getCustomerQueueVisibilityForAppointment(appointment);
                              if (vis === 'off') return null;
                              if (queueState === 'future') {
                                return (
                                  <Text style={styles.detailText}>
                                    Live Queue will be available on the appointment day.
                                  </Text>
                                );
                              }
                              if (queueState !== 'show') return null;
                              if (vis === 'status_only') {
                                return (
                                  <CustomerQueueStatusCard
                                    appointment={appointment}
                                    pickupPrefs={
                                      appointment.businessOwnerId
                                        ? businessAppointmentPrefs[appointment.businessOwnerId]
                                        : undefined
                                    }
                                    onRefresh={onQueueRefresh}
                                    isRefreshing={queueRefreshing}
                                    lastUpdatedAt={lastQueueRefreshAt}
                                  />
                                );
                              }
                              const queue = getQueueInsightForAppointment(appointment);
                              const bidQueue = appointment.businessOwnerId;
                              const ppFull = bidQueue ? businessAppointmentPrefs[bidQueue] : undefined;
                              return (
                                <View>
                                  <LiveQueueCard
                                    insight={queue}
                                    onRefresh={onQueueRefresh}
                                    isRefreshing={queueRefreshing}
                                    lastUpdatedAt={lastQueueRefreshAt}
                                  />
                                  {ppFull?.pickupReadyDisplayEnabled ? (
                                    <Text style={[styles.detailText, { marginTop: 6 }]}>
                                      Visit: {getCustomerQueueStatusLine(appointment, ppFull)}
                                    </Text>
                                  ) : null}
                                </View>
                              );
                            })()}
                            {appointment.vehicleHandoff ? (
                              <Text style={styles.detailText}>
                                🅿️ Visit type: {formatVehicleHandoffLabel(appointment.vehicleHandoff)}
                              </Text>
                            ) : null}
                            {appointment.vehicleYear && appointment.vehicleYear !== 'N/A' ? (
                              <>
                                <Text style={styles.detailText}>
                                  🚗 Vehicle: {appointment.vehicleYear} {appointment.vehicleMake}{' '}
                                  {appointment.vehicleModel}
                                </Text>
                                {appointment.vehicleLicensePlate ? (
                                  <Text style={styles.detailText}>
                                    🔖 Plate: {appointment.vehicleLicensePlate}
                                  </Text>
                                ) : null}
                              </>
                            ) : null}
                          </View>

                          {appointment.specialInstructions &&
                            (appointment.specialInstructions.includes('Quantity:') ||
                              appointment.specialInstructions.includes('Selected Options:') ||
                              appointment.specialInstructions.includes('×')) && (
                            <View style={{ marginTop: 8, padding: 12, backgroundColor: '#F8F9FA', borderRadius: 8 }}>
                              <Text style={[styles.detailText, { fontWeight: 'bold', marginBottom: 6, fontSize: 14 }]}>
                                📦 Product Order Details:
                              </Text>
                              {(() => {
                                const productStrings = appointment.specialInstructions.split(' || ');
                                return (
                                  <>
                                    {productStrings.map((productString, productIdx) => {
                                      const parts = productString.split(' | ');
                                      let productName = appointment.serviceType;
                                      let quantity = '';
                                      let options = '';
                                      parts.forEach((part) => {
                                        if (part.includes('Quantity:')) {
                                          const match = part.match(/Quantity:\s*(\d+)\s*×\s*(.+)/);
                                          if (match) {
                                            quantity = match[1];
                                            productName = match[2].trim();
                                          } else {
                                            quantity = part.replace('Quantity:', '').trim();
                                          }
                                        } else if (part.includes('Selected Options:')) {
                                          options = part.replace('Selected Options:', '').trim();
                                        }
                                      });
                                      const optionsList = options ? options.split(',').map((opt) => opt.trim()).filter(Boolean) : [];
                                      return (
                                        <View key={productIdx} style={{ marginBottom: productIdx < productStrings.length - 1 ? 12 : 0 }}>
                                          {productStrings.length > 1 && (
                                            <Text style={[styles.detailText, { marginBottom: 4, fontWeight: 'bold', fontSize: 14 }]}>
                                              Product {productIdx + 1}:
                                            </Text>
                                          )}
                                          <Text style={[styles.detailText, { marginBottom: 4 }]}>🛍️ Product: {productName}</Text>
                                          {quantity && (
                                            <Text style={[styles.detailText, { marginBottom: 4 }]}>🔢 Quantity: {quantity}</Text>
                                          )}
                                          {optionsList.length > 0 && (
                                            <View style={{ marginBottom: 4 }}>
                                              <Text style={[styles.detailText, { marginBottom: 2, fontWeight: '600' }]}>
                                                ⚙️ Product Options:
                                              </Text>
                                              {optionsList.map((option, idx) => (
                                                <Text key={idx} style={[styles.detailText, { marginLeft: 12, marginBottom: 2 }]}>
                                                  • {option}
                                                </Text>
                                              ))}
                                            </View>
                                          )}
                                        </View>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </View>
                          )}

                          {appointment.status === 'confirmed' && (
                            <View style={styles.actionButtons}>
                              <TouchableOpacity
                                style={[styles.actionButton, styles.rescheduleButton]}
                                onPress={() => handleRescheduleAppointment(appointment)}
                              >
                                <View style={styles.actionButtonContent}>
                                  <Image source={require('../assets/images/bookapt-logo.png')} style={styles.actionButtonIcon} resizeMode="contain" />
                                  <Text style={styles.actionButtonText}>Reschedule</Text>
                                </View>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={[styles.actionButton, styles.cancelButton]}
                                onPress={() => handleCancelAppointment(appointment)}
                              >
                                <Text style={[styles.actionButtonText, styles.cancelButtonText]}>❌ Cancel</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={[styles.actionButton, styles.deleteButton]}
                                onPress={() => handleDeleteAppointment(appointment)}
                              >
                                <Text style={styles.actionButtonText}>🗑 Delete</Text>
                              </TouchableOpacity>
                            </View>
                          )}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ))
          )}
        </View>

        {/* No Appointments */}
        {effectiveAppointments.length === 0 && (
          <View style={styles.noAppointmentsContainer}>
            {/* Book via Chat Button - Prominent option at top */}
            <TouchableOpacity 
              style={styles.bookViaChatButton}
              onPress={() => {
                if (onNavigateToChat) {
                  onNavigateToChat();
                }
              }}
            >
              <Text style={styles.bookViaChatIcon}>💬</Text>
              <Text style={styles.bookViaChatTitle}>Book via Chat</Text>
            </TouchableOpacity>

            <Text style={styles.noAppointmentsTitle}>No Appointments Yet</Text>
            <Text style={styles.noAppointmentsText}>
              You haven't booked any appointments yet. Start by chatting with businesses or exploring nearby options!
            </Text>
            <TouchableOpacity 
              style={styles.exploreButton}
              onPress={() => {
                if (onExploreBusinesses) {
                  onExploreBusinesses();
                } else if (onTabPress) {
                  // Fallback: navigate to favorites tab which has explore businesses
                  onTabPress('favorites');
                }
              }}
            >
              <Text style={styles.exploreButtonText}>Explore Businesses</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      {/* Bottom Menu */}
      {onTabPress && (
        <UserBottomMenu
          activeTab="appointments"
          onTabPress={handleTabPress}
          onLogout={onLogout}
        />
      )}

      {/* Edit Appointment Modal */}
      <EditAppointmentModal
        visible={showEditModal}
        booking={editingAppointment}
        onClose={() => {
          setShowEditModal(false);
          setEditingAppointment(null);
        }}
        onSave={handleSaveEditedAppointment}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingTop: 50,
    backgroundColor: '#007AFF',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  backButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 70, // Space for bottom menu (consistent across all screens)
    flexGrow: 1,
    minHeight: '100%', // Ensure content takes full height even when empty
  },
  filterRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 10,
    gap: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  filterDropdown: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    justifyContent: 'center',
  },
  filterDropdownText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-start',
    paddingTop: 100,
    paddingHorizontal: 20,
  },
  filterDropdownMenu: {
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    minWidth: 200,
  },
  filterDropdownItem: {
    paddingVertical: 14,
    paddingHorizontal: 18,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#eee',
  },
  filterDropdownItemText: {
    fontSize: 16,
    color: '#333',
  },
  filterDropdownItemTextActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  exportDropdownMenu: {
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    minWidth: 240,
  },
  exportDropdownItem: {
    paddingVertical: 14,
    paddingHorizontal: 18,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#eee',
  },
  exportDropdownItemText: {
    fontSize: 16,
    color: '#333',
  },
  emptyState: {
    padding: 24,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
  },
  sectionContainer: {
    padding: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 0,
  },
  pastSubtitle: {
    fontSize: 12,
    color: '#999',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  exportButton: {
    backgroundColor: '#28a745',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  exportButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  appointmentCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  dateSectionBlock: {
    marginBottom: 8,
  },
  dateSectionHeading: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginTop: 4,
    marginBottom: 2,
  },
  dateSectionCount: {
    fontSize: 12,
    color: '#8E8E93',
    marginBottom: 10,
  },
  compactAppointmentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  compactLeft: {
    flex: 1,
    minWidth: 0,
  },
  compactTime: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  compactService: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  compactBusiness: {
    fontSize: 13,
    color: '#8E8E93',
  },
  compactRight: {
    alignItems: 'flex-end',
    maxWidth: '46%',
  },
  compactBadgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 6,
    alignItems: 'center',
  },
  compactTapHint: {
    marginTop: 6,
    fontSize: 11,
    color: '#8E8E93',
  },
  expandedAppointmentBlock: {
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E5E5EA',
  },
  appointmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  serviceName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  appointmentDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  bookaptIconSmall: {
    width: 18,
    height: 18,
    marginRight: 8,
  },
  detailText: {
    fontSize: 14,
    color: '#666',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
  },
  actionButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    flex: 1,
    alignItems: 'center',
  },
  rescheduleButton: {
    backgroundColor: '#007AFF',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#FF3B30',
  },
  deleteButton: {
    backgroundColor: '#842029',
  },
  actionButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButtonIcon: {
    width: 18,
    height: 18,
    marginRight: 6,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  cancelButtonText: {
    color: '#FF3B30',
  },
  completedActions: {
    flexDirection: 'row',
    gap: 8,
  },
  rateButton: {
    backgroundColor: '#FF9500',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    flex: 1,
    alignItems: 'center',
  },
  rateButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  bookAgainButton: {
    backgroundColor: '#34C759',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    flex: 1,
    alignItems: 'center',
  },
  bookAgainButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  bookViaChatButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  bookViaChatIcon: {
    fontSize: 36,
    marginBottom: 8,
  },
  bookViaChatTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  bookViaChatText: {
    fontSize: 13,
    color: '#FFFFFF',
    opacity: 0.9,
    textAlign: 'center',
  },
  noAppointmentsContainer: {
    alignItems: 'center',
    padding: 40,
    marginHorizontal: 20,
  },
  noAppointmentsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  noAppointmentsText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  exploreButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  exploreButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default UserAppointmentsScreen;
